import React, { useCallback, useMemo } from "react";
import { Group, NumberInput, Select, Text } from "@mantine/core";
import {
  RELATIVE_DATE_UNITS,
  RELATIVE_DATE_UNIT_LABELS,
  parseRelativeDate,
  isWindowDateOperator,
  RelativeDateUnit,
} from "../constants/segmentConstants";

type RelativeDateInputProps = {
  value: string;
  onChange: (value: string) => void;
  /**
   * The rule's operator. Bounded window operators (within_next/within_last) already
   * read as "Within the next/last …", so the trailing "ago" suffix is hidden for them.
   */
  operator?: string;
};

/**
 * Input component for relative date values.
 * Shows a number input + unit dropdown, plus an "ago" suffix for the legacy
 * open-ended operators (the bounded window operators supply their own direction).
 * Outputs combined string like "30d", "2w", "3m", "1y".
 */
const RelativeDateInput: React.FC<RelativeDateInputProps> = ({
  value,
  onChange,
  operator,
}) => {
  const parsed = useMemo(() => parseRelativeDate(value), [value]);

  const amount = parsed?.amount ?? 1;
  const unit = parsed?.unit ?? "d";

  const handleAmountChange = useCallback(
    (newAmount: number | undefined) => {
      // Validate: must be a positive integer >= 1
      const validAmount =
        newAmount !== undefined && newAmount >= 1 ? Math.floor(newAmount) : 1;
      onChange(`${validAmount}${unit}`);
    },
    [unit, onChange]
  );

  const handleUnitChange = useCallback(
    (newUnit: string | null) => {
      if (newUnit) {
        onChange(`${amount}${newUnit as RelativeDateUnit}`);
      }
    },
    [amount, onChange]
  );

  const unitOptions = useMemo(
    () =>
      RELATIVE_DATE_UNITS.map((u) => {
        const labels = RELATIVE_DATE_UNIT_LABELS[u.value];
        // Use singular for 1, plural otherwise
        const label = amount === 1 ? labels.singular : labels.plural;
        // Capitalize first letter
        return {
          value: u.value,
          label: label.charAt(0).toUpperCase() + label.slice(1),
        };
      }),
    [amount]
  );

  return (
    <Group spacing="xs" noWrap align="center">
      <NumberInput
        placeholder="Amount"
        min={1}
        value={amount}
        onChange={handleAmountChange}
        style={{ flex: 1 }}
        parser={(val) => (val || "").replace(/[^\d]/g, "")}
        hideControls
      />
      <Select
        placeholder="Unit"
        data={unitOptions}
        value={unit}
        onChange={handleUnitChange}
        style={{ flex: 1 }}
      />
      {!isWindowDateOperator(operator ?? "") && (
        <Text size="sm" color="dimmed" style={{ whiteSpace: "nowrap" }}>
          ago
        </Text>
      )}
    </Group>
  );
};

export default RelativeDateInput;
